/**
 * The holdout: how many members receive no NON-CRITICAL LIFECYCLE EMAIL, so the
 * nudge rail can be measured against a group it does not mail.
 *
 * 🔴 It is not "a group that receives nothing", which is what this card said
 * until 2026-09-23. Cards, pushes and critical emails reach a holdout member
 * exactly as they reach anyone else. Every word of that is in `../data`, so
 * there is one copy of it and the test can pin it.
 *
 * Counts by arm and nothing else — this card never names a member.
 */
import {
  formatCount,
  formatRate,
  tileBody,
  HOLDOUT_ARM_LABELS,
  HOLDOUT_ASSIGNED_LABEL,
  HOLDOUT_ASSIGNED_NOTE,
  HOLDOUT_SUBTITLE,
  type HoldoutData,
  type Tile,
} from '../data';
import { TileUnavailable, tileShell } from '../../system-health/components/TileStatus';

export function HoldoutCard({ tile, loading }: { tile: Tile<HoldoutData>; loading: boolean }) {
  const unavailable = tile.status === 'unavailable';
  const body = tileBody(tile, loading);
  const data = tile.data;

  return (
    <section className={`border rounded-lg p-6 ${tileShell(unavailable, 'bg-card')}`}>
      <h3 className="text-lg font-semibold mb-1">Holdout</h3>
      <p className="text-xs text-muted-foreground mb-4">{HOLDOUT_SUBTITLE}</p>

      {unavailable && <TileUnavailable error={tile.error} />}

      {body === 'loading' && (
        <p className="text-sm text-muted-foreground">Loading the holdout…</p>
      )}

      {body === 'ready' && data && (
        <div className="grid gap-4 sm:grid-cols-3">
          {data.arms.map((a) => (
            <div key={a.arm}>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {HOLDOUT_ARM_LABELS[a.arm] ?? a.arm}
              </div>
              <div className="text-2xl font-semibold tabular-nums">{formatCount(a.members)}</div>
              <div className="text-[11px] text-muted-foreground tabular-nums">
                {formatRate(a.members, data.total) ?? '—'}
              </div>
            </div>
          ))}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {HOLDOUT_ASSIGNED_LABEL}
            </div>
            <div className="text-2xl font-semibold tabular-nums">{formatCount(data.total)}</div>
            <div className="text-[11px] text-muted-foreground">{HOLDOUT_ASSIGNED_NOTE}</div>
          </div>
        </div>
      )}
    </section>
  );
}
